"use client";

import { toast } from "@ui/components/toast";
import { cn } from "@ui/lib";
import {
	ArrowUpIcon,
	AudioLinesIcon,
	ChevronLeftIcon,
	LockIcon,
	MicIcon,
	SquareIcon,
	Trash2Icon,
} from "lucide-react";
import {
	type FormEvent,
	type PointerEvent,
	type RefObject,
	useEffect,
	useEffectEvent,
	useRef,
	useState,
} from "react";
import type { ShopperVoice } from "~/server/shopper";
import { useCopy } from "./copy-context";
import {
	formatDuration,
	MIN_NOTE_MS,
	useElapsed,
	useVoiceNote,
	type VoiceNote,
} from "./voice/hooks";
import { canRecord, type Transcribe } from "./voice/speech";

/** A press shorter than this is a tap: recording carries on hands-free. */
const TAP_MS = 350;
const CANCEL_DX = -90;
const LOCK_DY = -70;

/**
 * starting: the mic is opening (the permission prompt may be up).
 * holding:  finger down; letting go sends.
 * locked:   hands-free; the send button sends, the bin discards.
 */
type Hold = "idle" | "starting" | "holding" | "locked";

type VoiceError =
	| "permission"
	| "unsupported"
	| "too_short"
	| "no_audio"
	| "too_long"
	| "no_speech"
	| "rate_limited"
	| "paused"
	| "failed";

const VOICE_ERRORS: Record<
	VoiceError,
	{ title: string; description?: string }
> = {
	permission: {
		title: "Microphone access is off",
		description: "Allow the microphone for this site, then try again.",
	},
	unsupported: {
		title: "Voice doesn't work in this browser",
		description: "Type your message, or try Chrome or Safari.",
	},
	too_short: { title: "That was too short. Try again and say a bit more." },
	no_audio: {
		title: "The microphone didn't record anything",
		description: "Close other apps using it, then try again.",
	},
	too_long: {
		title: "That was over a minute",
		description: "Keep voice messages under a minute.",
	},
	no_speech: {
		title: "Couldn't catch that",
		description: "Try again a little closer to the phone.",
	},
	rate_limited: {
		title: "Too many voice messages. Try again in a minute.",
	},
	paused: {
		title: "The assistant can't reply right now",
		description: "Please try again later.",
	},
	failed: {
		title: "Couldn't read that clip",
		description: "Check your connection and try again.",
	},
};

/**
 * Text, voice and the door to live voice, in one row.
 *
 * One mic gesture that can't be done wrong: press it and it records. A tap
 * leaves it recording hands-free, with a send button and a bin; holding and
 * letting go sends straight away (slide left to cancel, up to go hands-free).
 * The server transcribes and works out the language. With voice notes on the
 * words are sent as a voice note; with only dictation on they land in the box
 * to edit.
 */
export function Composer({
	draft,
	setDraft,
	busy,
	shown,
	placeholder,
	voice,
	transcribe,
	onSend,
	onStop,
	onVoiceNote,
	onLive,
}: {
	draft: string;
	setDraft: (value: string | ((prev: string) => string)) => void;
	busy: boolean;
	/** The chat is on screen: nothing records while it is not. */
	shown: boolean;
	placeholder: string;
	voice: ShopperVoice;
	transcribe: Transcribe;
	onSend: (text: string) => boolean;
	onStop: () => void;
	onVoiceNote: (note: VoiceNote) => void;
	onLive: (() => void) | null;
}) {
	const copy = useCopy();
	const textarea = useRef<HTMLTextAreaElement | null>(null);
	const note = useVoiceNote(transcribe);
	const elapsed = useElapsed(note.startedAt, note.recording);

	const hold = useRef<{
		state: Hold;
		/** Let go while the mic was still opening. */
		released: boolean;
		pressedAt: number;
		x: number;
		y: number;
	}>({ state: "idle", released: false, pressedAt: 0, x: 0, y: 0 });
	const [holdState, setHoldState] = useState<Hold>("idle");
	const [drag, setDrag] = useState({ dx: 0, dy: 0 });

	const setHold = (state: Hold) => {
		hold.current.state = state;
		setHoldState(state);
	};

	// Checked after mount: the server render cannot know the browser.
	const [recordable, setRecordable] = useState(false);
	useEffect(() => setRecordable(canRecord()), []);
	const micEnabled = recordable && (voice.dictation || voice.voiceNotes);
	const hasText = draft.trim().length > 0;
	const transcribing = note.transcribing;

	const submit = (event?: FormEvent) => {
		event?.preventDefault();
		if (onSend(draft)) {
			setDraft("");
		}
	};

	const showError = (error: VoiceError) =>
		toast.add({
			type: error === "too_short" ? "info" : "warning",
			...VOICE_ERRORS[error],
		});

	const beginRecording = async () => {
		hold.current.released = false;
		setHold("starting");
		try {
			await note.start();
		} catch (error) {
			setHold("idle");
			showError(
				error instanceof DOMException &&
					error.name === "NotAllowedError"
					? "permission"
					: "unsupported",
			);
			return;
		}
		if (hold.current.state !== "starting") {
			// Cancelled while the mic was opening.
			note.cancel();
			return;
		}
		// A tap, or a hold that ended in the permission prompt: keep recording
		// hands-free rather than throw the take away.
		setHold(hold.current.released ? "locked" : "holding");
	};

	const sendRecording = async () => {
		setHold("idle");
		setDrag({ dx: 0, dy: 0 });
		const result = await note.finish();
		if (!result) {
			return;
		}
		if ("error" in result) {
			showError(result.error);
			return;
		}
		if (voice.voiceNotes) {
			onVoiceNote(result.note);
			return;
		}
		// Dictation only: the words go in the box to edit before sending.
		setDraft((prev) =>
			[prev.trim(), result.note.transcript].filter(Boolean).join(" "),
		);
		textarea.current?.focus({ preventScroll: true });
	};

	const cancelRecording = () => {
		setHold("idle");
		setDrag({ dx: 0, dy: 0 });
		note.cancel();
	};

	// The host closed the chat mid-recording: the microphone goes, the take with it. Starting
	// needs no check: it takes a press, and a press on a hidden chat shows it (shown.ts).
	const hiddenNow = useEffectEvent(() => {
		if (hold.current.state !== "idle") {
			cancelRecording();
		}
	});
	useEffect(() => {
		if (!shown) {
			hiddenNow();
		}
	}, [shown]);

	const micBlocked = hasText || busy || transcribing || !micEnabled;

	const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
		if (micBlocked || event.button !== 0 || holdState !== "idle") {
			return;
		}
		event.currentTarget.setPointerCapture(event.pointerId);
		hold.current.x = event.clientX;
		hold.current.y = event.clientY;
		hold.current.pressedAt = Date.now();
		void beginRecording();
	};

	const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
		if (hold.current.state !== "holding") {
			return;
		}
		const dx = Math.min(0, event.clientX - hold.current.x);
		const dy = Math.min(0, event.clientY - hold.current.y);
		setDrag({ dx, dy });
		if (dx < CANCEL_DX) {
			cancelRecording();
		} else if (dy < LOCK_DY) {
			setDrag({ dx: 0, dy: 0 });
			setHold("locked");
		}
	};

	const onPointerUp = () => {
		const state = hold.current.state;
		if (state === "starting") {
			hold.current.released = true;
		} else if (state === "holding") {
			// A tap, or a press let go before anything could be said: the mic takes a moment to
			// open (most of a second in an iPhone app's webview), so a half-second press sent a
			// clip too short to keep, every time (ADCOOP, 2026-10-24). It records hands-free instead.
			if (
				Date.now() - hold.current.pressedAt < TAP_MS ||
				Date.now() - note.startedAt < MIN_NOTE_MS
			) {
				setDrag({ dx: 0, dy: 0 });
				setHold("locked");
			} else {
				void sendRecording();
			}
		}
	};

	// The browser took the pointer (a system gesture, the permission sheet):
	// keep what was recorded and let the shopper decide.
	const onPointerCancel = () => {
		const state = hold.current.state;
		if (state === "starting") {
			hold.current.released = true;
		} else if (state === "holding") {
			setDrag({ dx: 0, dy: 0 });
			setHold("locked");
		}
	};

	const recording = holdState !== "idle";

	return (
		<div className="flex flex-col gap-2">
			{busy ? (
				// Not in the mic's place: a shopper reaching for the mic while a reply was
				// coming stopped it (ADCOOP, 2026-10-21), and it looked like the answer failed.
				<StopButton label={copy.stop} onStop={onStop} />
			) : null}
			{recording || transcribing ? (
				<RecordingStatus
					text={statusText(copy, transcribing, holdState)}
				/>
			) : null}

			<div className="flex items-end gap-2">
				{recording ? (
					<RecordingBar
						holding={holdState === "holding"}
						dragX={drag.dx}
						levels={note.levels}
						elapsed={elapsed}
						onCancel={cancelRecording}
					/>
				) : (
					<MessageForm
						textareaRef={textarea}
						draft={draft}
						setDraft={setDraft}
						placeholder={
							transcribing ? copy.transcribing : placeholder
						}
						onSubmit={submit}
						onLive={hasText ? null : onLive}
					/>
				)}

				<ComposerAction
					holdState={holdState}
					canSend={hasText || !micEnabled}
					sendDisabled={!hasText || busy}
					onSendRecording={sendRecording}
					onSubmit={() => submit()}
					mic={{
						holdState,
						drag,
						levels: note.levels,
						disabled: transcribing || busy,
						// Keyboard and switch access have no hold: Enter or Space
						// records hands-free. Pointers are handled on down/up.
						onKeyboardPress: () => {
							if (!micBlocked && holdState === "idle") {
								void beginRecording();
								hold.current.released = true;
							}
						},
						onPointerDown,
						onPointerMove,
						onPointerUp,
						onPointerCancel,
					}}
				/>
			</div>
		</div>
	);
}

/** The line above the composer while the mic is open or the clip is being read. */
function statusText(
	copy: ReturnType<typeof useCopy>,
	transcribing: boolean,
	holdState: Hold,
): string {
	if (transcribing) {
		return copy.transcribing;
	}
	if (holdState === "starting") {
		return copy.openingMic;
	}
	return holdState === "holding"
		? copy.letGoToSend
		: "Listening. Tap send when you're done.";
}

function StopButton({ label, onStop }: { label: string; onStop: () => void }) {
	return (
		<button
			type="button"
			onClick={onStop}
			className="press flex h-8 items-center gap-1.5 self-center rounded-(--shop-btn) bg-(--shop-card) px-3.5 font-medium text-(--shop-ink) text-sm outline-none ring-1 ring-(--shop-brand-line) focus-visible:ring-2 focus-visible:ring-(--shop-brand)/40"
		>
			<SquareIcon className="size-3 fill-current" aria-hidden />
			{label}
		</button>
	);
}

function RecordingStatus({ text }: { text: string }) {
	return (
		<div
			role="status"
			className="flex items-center gap-2 px-1 text-(--shop-muted) text-xs"
		>
			<span className="size-2 shrink-0 animate-pulse rounded-full bg-(--shop-deal)" />
			<span className="min-w-0 flex-1 truncate">{text}</span>
		</div>
	);
}

/** In the text box's place while recording: cancel (bin or slide), the levels, the time. */
function RecordingBar({
	holding,
	dragX,
	levels,
	elapsed,
	onCancel,
}: {
	holding: boolean;
	dragX: number;
	levels: number[];
	elapsed: number;
	onCancel: () => void;
}) {
	return (
		<div className="flex h-13 min-w-0 flex-1 items-center gap-3 rounded-(--shop-btn) bg-(--shop-card) ps-2 pe-4 ring-1 ring-(--shop-brand-line)">
			{holding ? (
				<span
					className="flex items-center gap-0.5 ps-2 font-medium text-(--shop-muted) text-xs transition-opacity"
					style={{ opacity: 1 + dragX / 140 }}
				>
					<ChevronLeftIcon className="size-4" aria-hidden />
					Slide to cancel
				</span>
			) : (
				<button
					type="button"
					onClick={onCancel}
					aria-label="Delete recording"
					className="flex size-9 items-center justify-center rounded-(--shop-btn) text-(--shop-muted) outline-none hover:bg-(--shop-brand-soft) focus-visible:ring-2 focus-visible:ring-(--shop-brand)/40"
				>
					<Trash2Icon className="size-4" />
				</button>
			)}
			<LiveLevels levels={levels} className="flex-1" />
			<span className="font-semibold text-(--shop-ink) text-sm tabular">
				{formatDuration(elapsed)}
			</span>
		</div>
	);
}

/** The text box, with the door to live voice while it is empty. */
function MessageForm({
	textareaRef,
	draft,
	setDraft,
	placeholder,
	onSubmit,
	onLive,
}: {
	textareaRef: RefObject<HTMLTextAreaElement | null>;
	draft: string;
	setDraft: (value: string) => void;
	placeholder: string;
	onSubmit: (event?: FormEvent) => void;
	onLive: (() => void) | null;
}) {
	return (
		<form
			onSubmit={onSubmit}
			className="flex min-w-0 flex-1 items-end rounded-[calc(var(--radius)*3.25)] bg-(--shop-card) p-1.5 ring-1 ring-(--shop-brand-line) transition-shadow focus-within:ring-2 focus-within:ring-(--shop-brand)/45"
		>
			<textarea
				ref={textareaRef}
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={(event) => {
					if (
						event.key === "Enter" &&
						!event.shiftKey &&
						!event.nativeEvent.isComposing
					) {
						event.preventDefault();
						onSubmit();
					}
				}}
				rows={1}
				dir="auto"
				enterKeyHint="send"
				aria-label="Message"
				placeholder={placeholder}
				// 16px text: iOS zooms the page into any smaller input.
				className="field-sizing-content max-h-36 min-h-10 flex-1 resize-none bg-transparent px-3 py-2 text-(--shop-ink) text-base leading-6 outline-none placeholder:text-(--shop-muted)"
			/>
			{onLive ? (
				<button
					type="button"
					onClick={onLive}
					aria-label="Talk live, hands-free"
					className="flex size-10 shrink-0 items-center justify-center rounded-(--shop-btn) text-(--shop-brand-text) outline-none hover:bg-(--shop-brand-soft) focus-visible:ring-2 focus-visible:ring-(--shop-brand)/40"
				>
					<AudioLinesIcon className="size-5" />
				</button>
			) : null}
		</form>
	);
}

/**
 * The button at the end of the row: send the hands-free recording, send the text (or the
 * mic is off), else the mic.
 */
function ComposerAction({
	holdState,
	canSend,
	sendDisabled,
	onSendRecording,
	onSubmit,
	mic,
}: {
	holdState: Hold;
	canSend: boolean;
	sendDisabled: boolean;
	onSendRecording: () => void;
	onSubmit: () => void;
	mic: MicButtonProps;
}) {
	if (holdState === "locked") {
		return (
			<RoundButton label="Send" onClick={onSendRecording}>
				<ArrowUpIcon className="size-5" strokeWidth={2.6} />
			</RoundButton>
		);
	}
	if (canSend) {
		return (
			<RoundButton
				label="Send"
				onClick={onSubmit}
				disabled={sendDisabled}
			>
				<ArrowUpIcon className="size-5" strokeWidth={2.6} />
			</RoundButton>
		);
	}
	return <MicButton {...mic} />;
}

interface MicButtonProps {
	holdState: Hold;
	drag: { dx: number; dy: number };
	/** The microphone's levels; the last one is the level now. */
	levels: number[];
	disabled: boolean;
	onKeyboardPress: () => void;
	onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
	onPointerMove: (event: PointerEvent<HTMLButtonElement>) => void;
	onPointerUp: () => void;
	onPointerCancel: () => void;
}

/** The press-to-talk button, with the lock hint above it while held. */
function MicButton({
	holdState,
	drag,
	levels,
	disabled,
	onKeyboardPress,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onPointerCancel,
}: MicButtonProps) {
	const recording = holdState !== "idle";
	const level = levels.at(-1) ?? 0;
	return (
		<div className="relative shrink-0">
			{holdState === "holding" ? (
				<span
					className="absolute -top-16 left-1/2 flex h-12 w-9 -translate-x-1/2 flex-col items-center justify-start rounded-full bg-(--shop-card) pt-2 text-(--shop-muted) shadow-[0_4px_16px_rgb(0_0_40/0.14)] transition-transform"
					style={{
						transform: `translate(-50%, ${drag.dy / 3}px)`,
					}}
					aria-hidden
				>
					<LockIcon className="size-4" />
				</span>
			) : null}
			{recording ? (
				<span
					aria-hidden
					className="absolute inset-0 animate-voice-ring rounded-full bg-(--shop-accent)"
				/>
			) : null}
			<button
				type="button"
				disabled={disabled}
				onClick={(event) => {
					// A click with no pointer behind it (detail 0) is a key press.
					if (event.detail === 0) {
						onKeyboardPress();
					}
				}}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerCancel}
				onContextMenu={(event) => event.preventDefault()}
				aria-label="Talk"
				title="Talk"
				aria-pressed={recording}
				className={cn(
					"relative flex size-13 touch-none select-none items-center justify-center rounded-(--shop-btn) outline-none transition-[transform,background-color] focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40 disabled:opacity-60",
					recording
						? "scale-110 bg-(--shop-accent) text-(--shop-accent-ink)"
						: "bg-(--shop-brand) text-(--shop-on-brand)",
				)}
				style={
					recording
						? {
								transform: `translate(${drag.dx}px, ${drag.dy}px) scale(${1.1 + level * 0.15})`,
							}
						: undefined
				}
			>
				<MicIcon className="size-5.5" strokeWidth={2.2} />
			</button>
		</div>
	);
}

function RoundButton({
	label,
	onClick,
	disabled,
	children,
}: {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			aria-label={label}
			onClick={onClick}
			disabled={disabled}
			className="press flex size-13 shrink-0 items-center justify-center rounded-(--shop-btn) bg-(--shop-brand) text-(--shop-on-brand) outline-none focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40 disabled:opacity-40"
		>
			{children}
		</button>
	);
}

const LIVE_BARS = 28;

/** Bars that follow the microphone while recording, newest on the right. */
function LiveLevels({
	levels,
	className,
}: {
	levels: number[];
	className?: string;
}) {
	const shown = [
		...Array.from({ length: LIVE_BARS - levels.length }, () => 0),
		...levels.slice(-LIVE_BARS),
	];
	return (
		<span
			aria-hidden
			className={cn(
				"flex h-7 min-w-0 items-center justify-end gap-[3px] overflow-hidden",
				className,
			)}
		>
			{shown.map((value, i) => (
				<span
					// Positions, not values: the bars shift left as levels arrive.
					key={i}
					style={{
						height: `${Math.round(Math.max(0.1, value) * 100)}%`,
					}}
					className="w-[3px] min-h-1 shrink-0 rounded-full bg-(--shop-brand) transition-[height] duration-100"
				/>
			))}
		</span>
	);
}
